import { FC, ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import './FiligraneBaseModal.scss';

export interface FiligraneBaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}

const FiligraneBaseModal: FC<FiligraneBaseModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
}) => {
  const [mounted, setMounted] = useState(isOpen);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      const raf = requestAnimationFrame(() =>
        requestAnimationFrame(() => setVisible(true))
      );
      return () => cancelAnimationFrame(raf);
    } else {
      setVisible(false);
      const t = setTimeout(() => setMounted(false), 280);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!mounted) return null;

  return createPortal(
    <div
      className={`fil-modal-overlay${visible ? ' fil-modal-overlay--visible' : ''}`}
      onClick={onClose}
    >
      <div
        className={`fil-modal fil-modal--${size}${visible ? ' fil-modal--visible' : ''}`}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'fil-modal-title' : undefined}
      >
        <div className="fil-modal__header">
          {title && (
            <h2 className="fil-modal__title" id="fil-modal-title">{title}</h2>
          )}
          <button className="fil-modal__close" onClick={onClose} aria-label="Fermer">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <path d="M1 1L11 11M11 1L1 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
        <div className="fil-modal__body">{children}</div>
      </div>
    </div>,
    document.body
  );
};

export default FiligraneBaseModal;
